import type { PlanEntry, ThreadEntry, ToolCallSnapshot, TurnProgressMessage, TurnSnapshotMessage } from "../../../api/threads/liveMessages";

/**
 * What a running turn has done so far (threads.md T-15): the agent's text, its tool calls
 * and its plan, or a tool's count of its work (F67) and the model fetched before it (V-4). Ephemeral: the server keeps it in memory only and the result is the
 * `turn_finished` entry, which replaces it. Updates carry a sequence number; an older or
 * repeated one changes nothing.
 */
export interface TurnProgressView {
  readonly turnId: string;
  readonly sequence: number;
  readonly messages: readonly string[];
  readonly toolCalls: readonly ToolCallSnapshot[];
  readonly plan: readonly PlanEntry[];
  /** A tool's latest count of its work, such as documents converted of all. */
  readonly work: WorkProgress | null;
  /** The bytes of the local model being fetched for a tool's run, before it starts (V-4); `null` once it is there. */
  readonly modelBytes: number | null;
}

export interface WorkProgress {
  readonly done: number;
  readonly total: number;
}

/**
 * Each open turn's progress, by turn ID (WD-4 L-2a, R3-2): an agent turn and the tool runs it
 * asked for (`run_tool`) report at once, each into its own view, so neither wipes the other.
 * Replaced, never changed, on update.
 */
export type TurnProgressMap = ReadonlyMap<string, TurnProgressView>;

export const NO_PROGRESS: TurnProgressMap = new Map();

/** The map with one turn's report folded into that turn's view. */
export function withTurnProgress(progress: TurnProgressMap, message: TurnProgressMessage): TurnProgressMap {
  return new Map(progress).set(message.turn_id, applyTurnProgress(progress.get(message.turn_id) ?? null, message));
}

/** The map with one turn's snapshot in place of an older view of it. */
export function withTurnSnapshot(progress: TurnProgressMap, snapshot: TurnSnapshotMessage): TurnProgressMap {
  const view = applyTurnSnapshot(progress.get(snapshot.turn_id) ?? null, snapshot);
  return view === null ? progress : new Map(progress).set(snapshot.turn_id, view);
}

/**
 * The views of the turns still open, by module C's word or the log's: a view goes once its turn's
 * result entry arrives (`finished`), or once the state no longer names it open (`open`, when given).
 */
export function retiredTurns(progress: TurnProgressMap, finished: ReadonlySet<string>, open: ReadonlySet<string> | null = null): TurnProgressMap {
  const kept = [...progress].filter(([turnId]) => !finished.has(turnId) && (open === null || open.has(turnId)));
  return kept.length === progress.size ? progress : new Map(kept);
}

/** The turns whose result entries are among `entries`. */
export function finishedTurns(entries: readonly ThreadEntry[]): ReadonlySet<string> {
  return new Set(
    entries.flatMap((entry) => (entry.payload.kind === "turn_finished" || entry.payload.kind === "tool_turn_finished" ? [entry.payload.turn_id] : [])),
  );
}

export function applyTurnSnapshot(current: TurnProgressView | null, snapshot: TurnSnapshotMessage): TurnProgressView | null {
  if (current !== null && current.turnId === snapshot.turn_id && current.sequence >= snapshot.progress_sequence) {
    return current;
  }
  return {
    turnId: snapshot.turn_id,
    sequence: snapshot.progress_sequence,
    messages: snapshot.messages,
    toolCalls: snapshot.tool_calls,
    plan: current?.turnId === snapshot.turn_id ? current.plan : [],
    work: snapshot.work === undefined || snapshot.work === null ? null : { done: snapshot.work.done, total: snapshot.work.total },
    modelBytes: snapshot.model_download == null || snapshot.model_download.finished ? null : snapshot.model_download.received_bytes,
  };
}

export function applyTurnProgress(current: TurnProgressView | null, progress: TurnProgressMessage): TurnProgressView {
  const base: TurnProgressView =
    current?.turnId === progress.turn_id ? current : { turnId: progress.turn_id, sequence: 0, messages: [], toolCalls: [], plan: [], work: null, modelBytes: null };
  if (progress.progress_sequence <= base.sequence) {
    return base;
  }
  let messages = [...base.messages];
  let toolCalls = [...base.toolCalls];
  let plan = base.plan;
  let work = base.work;
  let modelBytes = base.modelBytes;
  for (const event of progress.events) {
    switch (event.kind) {
      case "message_delta": {
        while (messages.length <= event.message_index) {
          messages.push("");
        }
        messages = messages.map((text, index) => (index === event.message_index ? text + event.text : text));
        break;
      }
      case "tool_call": {
        const snapshot: ToolCallSnapshot = {
          tool_call_id: event.tool_call_id,
          title: event.title,
          tool_kind: event.tool_kind,
          status: event.status,
        };
        const known = toolCalls.some((call) => call.tool_call_id === event.tool_call_id);
        toolCalls = known ? toolCalls.map((call) => (call.tool_call_id === event.tool_call_id ? snapshot : call)) : [...toolCalls, snapshot];
        break;
      }
      case "plan":
        plan = event.entries;
        break;
      case "work_progress":
        work = { done: event.done, total: event.total };
        break;
      case "model_download":
        modelBytes = event.finished ? null : event.received_bytes;
        break;
    }
  }
  return { turnId: base.turnId, sequence: progress.progress_sequence, messages, toolCalls, plan, work, modelBytes };
}
